// True pattern fills for the England designation layers (BBB-457), drawn
// client-side into an SVG overlay instead of asked for from Defra.
//
// WHY THIS EXISTS
//
// The England designations need a hatch, not a wash, because overlapping SSSI /
// SAC / SPA fills blend into a colour that appears nowhere in the legend, and
// because a colourblind reader needs a differentiator beyond hue. Two earlier
// routes reached for someone else's renderer:
//
//   1. Server-side inline SLD through the bbb-server tile proxy. Dead at the
//      geometry the client actually requests: Defra's GeoWebCache serves a
//      pre-rendered tile and ignores SLD_BODY on aligned 256x256, measured 8/8.
//      It only honours the style on an oversize request, which makes every tile
//      a deliberate cache miss and a live render at their end. Rejected 07/08.
//   2. google.maps.Polygon fillColor. Cannot express a pattern at all — a
//      Polygon takes one solid colour and an opacity, which is why the wide-area
//      layers are still drawn as outlines and only the designations are hatched.
//
// This is the third route, and it needs nobody's cooperation: we already fetch
// the polygon geometry for these layers (measured at 13 features and 118 KB for
// a farm-scale viewport), so we can draw it ourselves and fill it with an SVG
// <pattern>. That gives exact control of hatch angle, spacing and line weight,
// where the SLD route capped out at three marks (`shape://slash`, `backslash`
// and `times`) and SSSI, SAC and SPA already used all three. BBB-525: that
// limit used to be attributed here to `sldTemplates.js`, which went with the
// deferred proxy work in BBB-420 and no longer exists in this repo.
//
// This IS the rendering for the England designations, not a trial of one. Davin
// picked it by eye against the reference tiles on 18/08/2026 and confirmed it,
// so it carries no flag: any source declaring `hatchDirection` is drawn this
// way and there is no route back to the plain Polygon fill. The runtime kill
// switch it used to carry (`bbb:map-hatch`, `window.__bbbMapHatch`) was deleted
// on 24/08/2026 because a switch nobody should ever throw is just a way for the
// farmer-facing map to end up rendering something no one chose.

import { fetchViewportFeatures, polygonsFromFeatures } from './arcgisVectorOutlines';
import { reportError } from '../report';

/**
 * Side of the repeating pattern cell, in CSS pixels, at the zoomed-out end and
 * the zoomed-in end.
 *
 * WHY IT IS NOT ONE NUMBER. It was a flat 8 until 19/08/2026, when Davin looked
 * at the working hatch on a real map: *"for the english regulatories, i want you
 * to make the pattern fills less dense, they get realllyyy dense when i zoom in
 * a lot"*.
 *
 * The zoom half of that is worth stating precisely, because the obvious reading
 * is wrong. `patternUnits` is `userSpaceOnUse`, so the cell is a fixed number of
 * SCREEN pixels and the line pitch never actually changed with zoom. What changed
 * is coverage: zoomed out a designation is a small patch crossed by a few lines,
 * zoomed in it fills the viewport, so the eye meets a whole screen of 8px hatch.
 * There was no zoom-dependent defect, and a single wider pitch would have fixed
 * it at every zoom.
 *
 * He then asked for it to open up as well: 16 zoomed out, 18 to 20 zoomed in. So
 * this is a deliberate look rather than a fix, and the range is kept narrow on
 * purpose. A pitch that moved a long way during a zoom would read as the map
 * shifting under the farmer, and 16 to 20 rounds to five whole-pixel steps across
 * the whole range, so it changes rarely and never mid-gesture more than once.
 */
const CELL_MIN = 16;
const CELL_MAX = 20;

/**
 * Zoom levels the cell range is spread across. Below the first, the widest
 * coverage and the smallest cell; above the second, the field view and the most
 * open cell. 17 rather than FIELD_ZOOM 18 so the field view itself is settled at
 * the maximum rather than still changing as it arrives.
 */
const CELL_ZOOM_MIN = 12;
const CELL_ZOOM_MAX = 17;

/**
 * Pattern cell size for a zoom level, in CSS pixels.
 *
 * Rounded to whole pixels: a fractional cell puts the phase arithmetic (which is
 * a modulo of the same number) onto fractions too, and a sub-pixel lattice offset
 * is exactly the sort of thing that makes a hatch shimmer during a pan.
 *
 * @param {number} zoom Google Maps zoom level
 * @returns {number} cell side in CSS pixels
 */
export function cellForZoom(zoom) {
  if (typeof zoom !== 'number' || !Number.isFinite(zoom)) return CELL_MIN;
  if (zoom <= CELL_ZOOM_MIN) return CELL_MIN;
  if (zoom >= CELL_ZOOM_MAX) return CELL_MAX;

  const t = (zoom - CELL_ZOOM_MIN) / (CELL_ZOOM_MAX - CELL_ZOOM_MIN);
  return Math.round(CELL_MIN + t * (CELL_MAX - CELL_MIN));
}

/**
 * Hatch angles, in degrees clockwise from a vertical line.
 *
 * Named rather than numeric so a catalogue source reads as a direction the way
 * the SLD template keys did (`shape://slash` and friends), and so two layers
 * cannot silently end up on the same angle without it being visible here.
 *
 * Five distinct directions, where the SLD route had three. That was the reason
 * NVZ and safeguard could not be hatched without reusing an angle already spent
 * on a designation, which would have left colour as the only differentiator.
 */
export const HATCH_ANGLES = Object.freeze({
  slash: 45,
  backslash: -45,
  vertical: 0,
  horizontal: 90,
  cross: null,      // drawn as two lines rather than one, see patternMarkup
});

/**
 * The <defs> markup for one source's hatch.
 *
 * patternUnits is userSpaceOnUse so the cell is a fixed pixel size on screen
 * rather than a fraction of the shape's bounding box: a hatch that rescaled per
 * polygon would read as a different pattern on a small field than on a large
 * one, which defeats the point of using it as an identifier.
 *
 * @param {string} id unique per source, since several patterns share one <svg>
 * @param {object} source catalogue source
 * @param {{x: number, y: number}} [phase] pattern-lattice offset in pixels, so
 *   the hatch stays anchored to the ground across a pan. See `draw`.
 * @returns {string} SVG markup for a single <pattern> element
 */
export function patternMarkup(id, source, phase = { x: 0, y: 0 }, cell = CELL_MIN) {
  const colour = source.hatchColour ?? source.outlineColour ?? '#000000';
  const weight = source.hatchWeight ?? 1.5;
  const direction = source.hatchDirection ?? 'slash';
  const angle = HATCH_ANGLES[direction];

  // The single line runs down the MIDDLE of the cell, not along x=0.
  //
  // A stroke is centred on its path, so a line at x=0 has half its width outside
  // the tile, and pattern content is clipped to its own tile rather than bleeding
  // into the neighbour. The first cut drew it at x=0 and every directional hatch
  // therefore rendered at half weight: measured on 18/08 against a stand-in
  // aerial background, SSSI and SAC were effectively invisible while SPA, which
  // draws two corner-to-corner diagonals and has no edge to lose, came out fine.
  // That asymmetry is what gave it away.
  const mid = cell / 2;

  // `cross` is the one direction that cannot be expressed as a rotation of a
  // single line, so it draws both diagonals in the cell instead.
  //
  // Which means it lays down twice the ink per cell, and it is thinned to
  // compensate. Looked at on 18/08 against the other two at equal weight, SPA
  // read as very nearly a solid orange wash while SSSI and SAC stayed
  // comfortably see-through — and SPA has precedence 120, so it is the one
  // sitting on top of the farmer's field. A hatch that hides the ground is the
  // exact complaint this route exists to answer, so equal STROKE weight is the
  // wrong thing to hold constant; equal apparent density is.
  const crossWeight = Math.round(weight * 0.7 * 100) / 100;

  const lines =
    angle === null
      ? `<line x1="0" y1="0" x2="${cell}" y2="${cell}" stroke="${colour}" stroke-width="${crossWeight}" />` +
        `<line x1="${cell}" y1="0" x2="0" y2="${cell}" stroke="${colour}" stroke-width="${crossWeight}" />`
      : `<line x1="${mid}" y1="0" x2="${mid}" y2="${cell}" stroke="${colour}" stroke-width="${weight}" />`;

  // Translate before rotate: the phase is measured in container pixels (the
  // axes the container's own origin moved along), so it has to be applied in
  // that frame and the rotation applied to the already-shifted lattice. The
  // other order would rotate the offset too and the hatch would still crawl,
  // just diagonally.
  const parts = [];
  if (phase.x || phase.y) parts.push(`translate(${-phase.x} ${-phase.y})`);
  if (angle !== null && angle !== 0) parts.push(`rotate(${angle})`);
  const transform = parts.length ? ` patternTransform="${parts.join(' ')}"` : '';

  return (
    `<pattern id="${id}" width="${cell}" height="${cell}" patternUnits="userSpaceOnUse"${transform}>` +
    lines +
    `</pattern>`
  );
}

/**
 * Turn projected rings into an SVG path `d` string.
 *
 * One path per feature with every ring appended, so an inner ring becomes a
 * hole under `fill-rule="evenodd"`. That mirrors how the Polygon adapter hands
 * Google Maps trailing entries in `paths`.
 *
 * @param {Array<Array<{x: number, y: number}>>} rings already in local pixels
 * @returns {string}
 */
export function pathFromRings(rings) {
  return rings
    .map((ring) => {
      if (!ring.length) return '';
      const [first, ...rest] = ring;
      const move = `M ${first.x.toFixed(1)} ${first.y.toFixed(1)}`;
      const lines = rest.map((p) => `L ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
      return `${move} ${lines} Z`;
    })
    .filter(Boolean)
    .join(' ');
}

/**
 * An SVG hatch layer for one catalogue source.
 *
 * Deliberately the same shape as createVectorOutlineLayer — `refresh(map,
 * bounds, zoom)` and `clear()`, with refresh resolving to `{truncated}` or to
 * undefined when nothing was repainted — so useVectorLayerController can hold
 * either kind of handle without knowing which it has.
 *
 * @param {object} source catalogue source
 * @param {object} [deps] injection seam for tests: fetchViewportFeatures, OverlayView
 */
export function createSvgHatchLayer(source, deps = {}) {
  const fetchFeatures = deps.fetchViewportFeatures ?? fetchViewportFeatures;
  const OverlayViewCtor = deps.OverlayView ?? window.google?.maps?.OverlayView;

  const patternId = `bbb-hatch-${source.id}`;

  let overlay = null;
  let features = [];
  // Fallback for draw() when the map cannot be asked its zoom, which is the case
  // in the unit tests' fake map. Set on every refresh.
  let lastZoom = null;
  // Same monotonic-token guard as the Polygon adapter: a pan fires far faster
  // than these fetches resolve, and a late response must not repaint a viewport
  // the farmer has already left.
  let requestToken = 0;

  /**
   * Build the OverlayView subclass lazily.
   *
   * It cannot be defined at module scope because it extends a constructor that
   * only exists once the Maps JS API script has run, and this module is
   * imported during the initial bundle evaluation.
   */
  function makeOverlay() {
    class HatchOverlay extends OverlayViewCtor {
      constructor() {
        super();
        this.container = null;
      }

      onAdd() {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        // Designations sit above the base imagery but must not swallow the tap
        // that selects one of the farmer's own zones.
        div.style.pointerEvents = 'none';
        this.container = div;
        // overlayLayer draws above the raster ImageMapTypes that carry soil,
        // flood and the Scottish and Welsh layers, which is the order we want
        // and, unlike deck.gl's interleaved mode, is not subject to an open
        // z-ordering bug.
        this.getPanes().overlayLayer.appendChild(div);
      }

      onRemove() {
        this.container?.parentNode?.removeChild(this.container);
        this.container = null;
      }

      draw() {
        if (!this.container) return;
        const projection = this.getProjection();
        if (!projection) return;

        // Project every ring into div pixels first, then position one container
        // at the bounding box and draw relative to it. Absolute div coordinates
        // run to hundreds of thousands at high zoom, which SVG handles poorly.
        const shapes = [];
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        for (const { paths } of features) {
          const rings = [];
          for (const path of paths) {
            const ring = [];
            for (const { lat, lng } of path) {
              const point = projection.fromLatLngToDivPixel(
                new window.google.maps.LatLng(lat, lng),
              );
              if (!point) continue;
              ring.push(point);
              if (point.x < minX) minX = point.x;
              if (point.y < minY) minY = point.y;
              if (point.x > maxX) maxX = point.x;
              if (point.y > maxY) maxY = point.y;
            }
            if (ring.length) rings.push(ring);
          }
          if (rings.length) shapes.push(rings);
        }

        if (!shapes.length) {
          this.container.innerHTML = '';
          return;
        }

        // A little slack so a stroke sitting exactly on the bounding box is not
        // clipped in half.
        const pad = 2;
        minX -= pad;
        minY -= pad;
        const width = maxX - minX + pad;
        const height = maxY - minY + pad;

        const localised = shapes.map((rings) =>
          rings.map((ring) => ring.map((p) => ({ x: p.x - minX, y: p.y - minY }))),
        );

        // Anchor the pattern to the world rather than to this container, so the
        // hatch does not appear to crawl across the ground as the container
        // origin changes on pan. Without this the lines stay put on screen while
        // the map moves under them, which reads as a rendering fault.
        //
        // This goes onto the <pattern> as a patternTransform, NOT onto a wrapping
        // <g>. The first cut wrapped the paths in translate(-phase) and
        // translate(+phase), which cancel exactly and moved nothing: a group
        // transform shifts the geometry and drags the fill along with it, so it
        // can never change where the lattice sits relative to the shape. Only
        // the pattern's own transform can.

        // The cell has to be resolved here, not at refresh time. draw() also runs
        // on plain pans and on zoom frames where no refetch happened, and the
        // phase below is a modulo of this same number, so a cell that lagged the
        // map by a frame would put the lattice offset out of step with the
        // lattice itself and the hatch would jump.
        const cell = cellForZoom(this.getMap()?.getZoom?.() ?? lastZoom);

        const phaseX = ((minX % cell) + cell) % cell;
        const phaseY = ((minY % cell) + cell) % cell;

        this.container.style.left = `${minX}px`;
        this.container.style.top = `${minY}px`;
        this.container.style.width = `${width}px`;
        this.container.style.height = `${height}px`;

        const paths = localised
          .map(
            (rings) =>
              `<path d="${pathFromRings(rings)}" fill="url(#${patternId})" fill-rule="evenodd" ` +
              `stroke="${source.outlineColour ?? '#000000'}" stroke-width="${source.outlineWeight ?? 2}" />`,
          )
          .join('');

        this.container.innerHTML =
          `<svg width="${width}" height="${height}" style="position:absolute;left:0;top:0;overflow:visible">` +
          `<defs>${patternMarkup(patternId, source, { x: phaseX, y: phaseY }, cell)}</defs>` +
          paths +
          `</svg>`;
      }
    }

    return new HatchOverlay();
  }

  function clear() {
    requestToken += 1;
    features = [];
    if (overlay) {
      overlay.setMap(null);
      overlay = null;
    }
  }

  /**
   * Fetch the viewport and repaint.
   *
   * @returns {Promise<{truncated: boolean}|undefined>} undefined when nothing
   *   was repainted, so the caller leaves the existing status alone.
   */
  async function refresh(targetMap, bounds, zoom) {
    const token = ++requestToken;

    let fetched;
    let truncated;
    try {
      ({ features: fetched, truncated } = await fetchFeatures(source, bounds, zoom));
    } catch (err) {
      // A failed designation fetch must not blank the map or take its siblings
      // down with it. Leave the previous render in place.
      //
      // A developer diagnostic only, and only since BBB-527: before the health
      // tracker existed this line was the sole trace that the layer had failed.
      // Now the farmer is told "Not available right now", so this being silent
      // outside a developer build costs nothing.
      reportError(`[${source.id}] hatch fetch failed:`, err);
      return undefined;
    }

    if (token !== requestToken) return undefined;

    lastZoom = zoom;
    features = polygonsFromFeatures(fetched, source.nameField);

    if (!overlay) {
      overlay = makeOverlay();
      overlay.setMap(targetMap);
    } else {
      overlay.draw();
    }

    return { truncated: Boolean(truncated) };
  }

  // `destroy` has to be here even though it only aliases `clear`.
  // useVectorLayerController calls handle.destroy() in two places, the reconcile
  // effect and the unmount cleanup, so a handle without it throws the moment a
  // layer is toggled off. The Polygon adapter aliases it the same way.
  return { refresh, clear, destroy: clear };
}
